package com.devi3east.bashbuddy.dashboard.ui.components.dialogs

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Info
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import coil3.compose.SubcomposeAsyncImage
import com.devi3east.bashbuddy.dashboard.ui.components.dropdowns.RaffleDropdown
import com.devi3east.bashbuddy.dashboard.ui.components.inputFields.CurrencyTextField
import com.devi3east.bashbuddy.dashboard.ui.components.inputFields.InputTextField
import com.devi3east.bashbuddy.dashboard.ui.components.inputFields.NumberTextField
import com.devi3east.bashbuddy.features.common.util.FilePicker
import com.devi3east.bashbuddy.features.events.common.data.models.RaffleType
import com.devi3east.bashbuddy.features.events.common.data.models.Ticket
import com.devi3east.bashbuddy.features.events.common.data.models.TicketStatus
import com.devi3east.bashbuddy.features.events.common.data.models.TicketType
import com.devi3east.bashbuddy.features.events.requests.UpdateTicketRequest
import com.devi3east.bashbuddy.prizeList.domain.models.PrizeType
import com.devi3east.bashbuddy.prizeList.requests.CreatePrizeRequest
import com.devi3east.bashbuddy.prizeList.requests.UpdatePrizeRequest
import kotlin.random.Random

@Composable
fun UpdateTicketDialog(
    token: String,
    ticket: Ticket,
    prizeId: String?,
    onDismiss: () -> Unit,
    onSubmit: (UpdateTicketRequest) -> Unit,
    onAddSideRaffle: (String, CreatePrizeRequest) -> Unit,
    onUpdateSideRaffle: (String, UpdatePrizeRequest) -> Unit,
    onTicketInfoClick: () -> Unit,
    onTicketImageUploaded: (String, String) -> Unit
) {
    val canEditAllFields = ticket.status == TicketStatus.DRAFT
    val canEditLimitedFields = ticket.status == TicketStatus.UPCOMING

    var selectedTicketType by remember { mutableStateOf(ticket.ticketType) }
    var selectedRaffleType by remember { mutableStateOf(ticket.raffleType) }
    var isFiftyFifty by remember { mutableStateOf(ticket.isFiftyFifty) }
    var title by remember { mutableStateOf(ticket.title ?: "") }
    var description by remember { mutableStateOf(ticket.description ?: "") }
    var imageUrl by remember { mutableStateOf(ticket.imageUrl ?: "") }
    var serialNumber by remember { mutableStateOf(ticket.serialNumber ?: "") }
    var chancesPerTicket by remember { mutableStateOf(ticket.chancesPerTicket?.toString() ?: "") }
    var maxQuantity by remember { mutableStateOf(ticket.maxQuantity?.toString() ?: "") }
    var availableQuantity by remember { mutableStateOf(ticket.availableQuantity?.toString() ?: "") }
    var donationCents by remember { mutableStateOf(ticket.donation) }
    var shippingFeeCents by remember { mutableStateOf(ticket.shippingFee) }
    var purchaseAge by remember { mutableStateOf(ticket.purchaseAge?.toString() ?: "18") }

    var showSideRaffleInfo by remember { mutableStateOf(false) }
    var showSerialNumberInfo by remember { mutableStateOf(false) }
    var showAgeInfo by remember { mutableStateOf(false) }
    var showShippingInfo by remember { mutableStateOf(false) }

    var selectedRaffleOption by remember {
        mutableStateOf(
            when {
                isFiftyFifty -> "50/50"
                selectedRaffleType == RaffleType.SIDE_RAFFLE -> "Side Raffle"
                else -> "Main Event"
            }
        )
    }

    val scrollState = rememberScrollState()

    AlertDialog(
        onDismissRequest = onDismiss,
        modifier = Modifier.imePadding(),
        title = {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.fillMaxWidth()
            ) {
                val action = if (canEditLimitedFields) "Edit" else "Create"
                val typeText = when (selectedTicketType) {
                    TicketType.DIGITAL -> "Digital Ticket"
                    TicketType.PRINTED -> "Printed Ticket"
                }

                Text(
                    text = "$action $typeText",
                    style = MaterialTheme.typography.titleLarge.copy(
                        fontWeight = FontWeight.SemiBold,
                        color = MaterialTheme.colorScheme.onSurface
                    ),
                )

                Spacer(Modifier.weight(1f))

                IconButton(
                    onClick = {
                        onTicketInfoClick()
                        onDismiss()
                    }
                ) {
                    Icon(
                        imageVector = Icons.Default.Info,
                        contentDescription = "Info",
                        tint = MaterialTheme.colorScheme.primary
                    )
                }
            }
        },
        text = {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .verticalScroll(scrollState)
                    .padding(vertical = 8.dp),
                verticalArrangement = Arrangement.spacedBy(10.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                // Ticket Type Buttons
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    listOf(TicketType.DIGITAL, TicketType.PRINTED).forEach { type ->
                        val isSelected = selectedTicketType == type
                        Button(
                            onClick = { selectedTicketType = type },
                            enabled = canEditAllFields,
                            colors = ButtonDefaults.buttonColors(
                                containerColor = if (isSelected)
                                    MaterialTheme.colorScheme.primary
                                else MaterialTheme.colorScheme.surfaceVariant,
                                contentColor = if (isSelected)
                                    MaterialTheme.colorScheme.onPrimary
                                else MaterialTheme.colorScheme.onSurface
                            ),
                            modifier = Modifier.weight(1f),
                            shape = RoundedCornerShape(12.dp)
                        ) {
                            Text(type.name.uppercase(), fontWeight = FontWeight.Medium)
                        }
                    }
                }

                // Title
                InputTextField(
                    label = "Title",
                    value = title,
                    onValueChange = { title = it },
                    modifier = Modifier.fillMaxWidth(),
                    enabled = canEditAllFields || canEditLimitedFields
                )

                // DIGITAL layout
                if (selectedTicketType == TicketType.DIGITAL) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        CurrencyTextField(
                            label = "Ticket Cost",
                            valueCents = donationCents,
                            onValueChange = { donationCents = it },
                            modifier = Modifier.weight(1f),
                            enabled = canEditAllFields
                        )
                        NumberTextField(
                            label = "Max Quantity",
                            value = maxQuantity,
                            onValueChange = { maxQuantity = it },
                            modifier = Modifier.weight(1f),
                            enabled = canEditAllFields
                        )
                    }

                    NumberTextField(
                        label = "Chances per Ticket",
                        value = chancesPerTicket,
                        onValueChange = { chancesPerTicket = it },
                        modifier = Modifier.fillMaxWidth(),
                        enabled = canEditAllFields
                    )

                    NumberTextField(
                        label = "Age Restriction",
                        value = purchaseAge,
                        onValueChange = { purchaseAge = it },
                        modifier = Modifier.fillMaxWidth(),
                        enabled = canEditAllFields,
                        trailingIcon = {
                            IconButton(onClick = { showAgeInfo = true }) {
                                Icon(
                                    imageVector = Icons.Default.Info,
                                    contentDescription = "Age Restriction Info",
                                    tint = MaterialTheme.colorScheme.primary
                                )
                            }
                        }
                    )

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        // Make dropdown take remaining width
                        RaffleDropdown(
                            selectedOption = selectedRaffleOption,
                            options = listOf("Main Event", "Side Raffle", "50/50"),
                            onOptionSelected = { option ->
                                selectedRaffleOption = option
                                when (option) {
                                    "Main Event" -> {
                                        selectedRaffleType = RaffleType.MAIN_RAFFLE
                                        isFiftyFifty = false
                                    }
                                    "Side Raffle" -> {
                                        selectedRaffleType = RaffleType.SIDE_RAFFLE
                                        isFiftyFifty = false
                                    }
                                    "50/50" -> {
                                        selectedRaffleType = RaffleType.SIDE_RAFFLE
                                        isFiftyFifty = true
                                    }
                                }
                            },
                            modifier = Modifier.weight(1f),
                            enabled = canEditAllFields
                        )

                        IconButton(onClick = { showSideRaffleInfo = true }) {
                            Icon(
                                imageVector = Icons.Default.Info,
                                contentDescription = "Raffle Info",
                                tint = MaterialTheme.colorScheme.primary
                            )
                        }
                    }

                    InputTextField(
                        label = "Description",
                        value = description,
                        onValueChange = { description = it },
                        modifier = Modifier
                            .fillMaxWidth()
                            .heightIn(min = 300.dp),
                        singleLine = false,
                        enabled = canEditAllFields || canEditLimitedFields
                    )

                    if (imageUrl.isNotBlank()) {
                        SubcomposeAsyncImage(
                            model = imageUrl,
                            contentDescription = "Ticket Image",
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(150.dp)
                                .clip(RoundedCornerShape(12.dp)),
                            contentScale = ContentScale.Crop
                        )
                    }

                    FilePicker(
                        token = token,
                        buttonText = "Select Image",
                        organizationId = ticket.createdById,
                        relatedTitle = ticket._id,
                        relatedType = "ticket",
                        onFileUploaded = { uploadedUrl ->
                            onTicketImageUploaded(ticket._id, uploadedUrl) // <-- new
                            imageUrl = uploadedUrl              // update preview locally
                        },
                        onUploadError = { },
                        modifier = Modifier.fillMaxWidth().height(48.dp)
                    )
                }

                // PRINTED layout
                if (selectedTicketType == TicketType.PRINTED) {
                    InputTextField(
                        label = "Serial Number",
                        value = serialNumber,
                        onValueChange = { if (canEditAllFields) serialNumber = it },
                        modifier = Modifier.fillMaxWidth(),
                        enabled = canEditAllFields,
                        trailingIcon = {
                            IconButton(onClick = { showSerialNumberInfo = true }) {
                                Icon(
                                    imageVector = Icons.Default.Info,
                                    contentDescription = "Serial Number Info",
                                    tint = MaterialTheme.colorScheme.primary
                                )
                            }
                        }
                    )

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        CurrencyTextField(
                            label = "Ticket Cost",
                            valueCents = donationCents,
                            onValueChange = { donationCents = it },
                            modifier = Modifier.weight(1f),
                            enabled = canEditAllFields
                        )
                        CurrencyTextField(
                            label = "Shipping Fee",
                            valueCents = shippingFeeCents,
                            onValueChange = { shippingFeeCents = it },
                            modifier = Modifier.weight(1f),
                            enabled = canEditAllFields || canEditLimitedFields,
                        )
                    }

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        NumberTextField(
                            label = "Quantity",
                            value = availableQuantity,
                            onValueChange = { availableQuantity = it },
                            modifier = Modifier.weight(1f),
                            enabled = canEditAllFields
                        )
                        NumberTextField(
                            label = "Max Quantity",
                            value = maxQuantity,
                            onValueChange = { maxQuantity = it },
                            modifier = Modifier.weight(1f),
                            enabled = canEditAllFields
                        )
                    }

                    NumberTextField(
                        label = "Chances per Ticket",
                        value = chancesPerTicket,
                        onValueChange = { chancesPerTicket = it },
                        modifier = Modifier.fillMaxWidth(),
                        enabled = canEditAllFields
                    )

                    NumberTextField(
                        label = "Age Restriction",
                        value = purchaseAge,
                        onValueChange = { purchaseAge = it },
                        modifier = Modifier.fillMaxWidth(),
                        enabled = canEditAllFields,
                        trailingIcon = {
                            IconButton(onClick = { showAgeInfo = true }) {
                                Icon(
                                    imageVector = Icons.Default.Info,
                                    contentDescription = "Age Restriction Info",
                                    tint = MaterialTheme.colorScheme.primary
                                )
                            }
                        }
                    )

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        // Make dropdown take remaining width
                        RaffleDropdown(
                            selectedOption = selectedRaffleOption,
                            options = listOf("Main Event", "Side Raffle", "50/50"),
                            onOptionSelected = { option ->
                                selectedRaffleOption = option
                                when (option) {
                                    "Main Event" -> {
                                        selectedRaffleType = RaffleType.MAIN_RAFFLE
                                        isFiftyFifty = false
                                    }
                                    "Side Raffle" -> {
                                        selectedRaffleType = RaffleType.SIDE_RAFFLE
                                        isFiftyFifty = false
                                    }
                                    "50/50" -> {
                                        selectedRaffleType = RaffleType.SIDE_RAFFLE
                                        isFiftyFifty = true
                                    }
                                }
                            },
                            modifier = Modifier.weight(1f),
                            enabled = canEditAllFields
                        )

                        IconButton(onClick = { showSideRaffleInfo = true }) {
                            Icon(
                                imageVector = Icons.Default.Info,
                                contentDescription = "Raffle Info",
                                tint = MaterialTheme.colorScheme.primary
                            )
                        }
                    }

                    InputTextField(
                        label = "Description",
                        value = description,
                        onValueChange = { description = it },
                        modifier = Modifier
                            .fillMaxWidth()
                            .heightIn(min = 300.dp),
                        singleLine = false,
                        enabled = canEditAllFields || canEditLimitedFields
                    )

                    if (imageUrl.isNotBlank()) {
                        SubcomposeAsyncImage(
                            model = imageUrl,
                            contentDescription = "Ticket Image",
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(150.dp)
                                .clip(RoundedCornerShape(12.dp)),
                            contentScale = ContentScale.Crop
                        )
                    }

                    FilePicker(
                        token = token,
                        buttonText = "Select Image",
                        organizationId = ticket.createdById,
                        relatedTitle = ticket._id,
                        relatedType = "ticket",
                        onFileUploaded = { uploadedUrl ->
                            onTicketImageUploaded(ticket._id, uploadedUrl)
                            imageUrl = uploadedUrl
                        },
                        onUploadError = { },
                        modifier = Modifier.fillMaxWidth().height(48.dp)
                    )
                }
            }
        },
        confirmButton = {
            Button(
                onClick = {
                    // Generate serial for digital tickets only
                    val generatedSerialNumber = if (selectedTicketType == TicketType.DIGITAL) {
                        val prefix = ticket.createdByName
                            ?.take(3)
                            ?.replace("\\s+".toRegex(), "")
                            ?.uppercase()
                            ?: "ORG"

                        val eventTag = ticket.eventTitle
                            ?.take(3)
                            ?.replace("\\s+".toRegex(), "")
                            ?.uppercase()
                            ?: "EVT"

                        // Custom safe character set (no 0, O, I, l, 1)
                        val safeChars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"

                        // Generate random readable code
                        val randomSuffix = buildString {
                            repeat(5) { append(safeChars.random()) } // 5 letters/numbers
                            append('-')
                            repeat(3) { append(Random.nextInt(2, 10)) } // extra digits (2–9)
                        }

                        "$prefix$eventTag-$randomSuffix"
                    } else serialNumber

                    val request = UpdateTicketRequest(
                        title = title,
                        description = description,
                        imageUrl = imageUrl.takeIf { it.isNotBlank() },
                        serialNumber = generatedSerialNumber,
                        chancesPerTicket = chancesPerTicket.toIntOrNull(),
                        maxQuantity = maxQuantity.toIntOrNull(),
                        availableQuantity = availableQuantity.toIntOrNull(),
                        donation = donationCents,
                        shippingFee = if (selectedTicketType == TicketType.PRINTED) shippingFeeCents else 0L,
                        ticketType = selectedTicketType,
                        raffleType = selectedRaffleType,
                        isFiftyFifty = isFiftyFifty
                    )

                    onSubmit(request)
                    onDismiss()

                    if (selectedRaffleType == RaffleType.SIDE_RAFFLE) {
                        if (prizeId != null) {
                            onUpdateSideRaffle(
                                prizeId,
                                UpdatePrizeRequest(
                                    prizeType = PrizeType.SIDE_RAFFLE,
                                    title = title
                                )
                            )
                        } else {
                            onAddSideRaffle(
                                ticket.eventId!!,
                                CreatePrizeRequest(
                                    prizeType = PrizeType.SIDE_RAFFLE,
                                    title = title,
                                    serialNumber = generatedSerialNumber,
                                    ticketId = ticket._id
                                )
                            )
                        }
                    }
                },
                shape = RoundedCornerShape(12.dp),
                colors = ButtonDefaults.buttonColors(
                    containerColor = MaterialTheme.colorScheme.primary,
                    contentColor = MaterialTheme.colorScheme.onPrimary
                )
            ) {
                Text("Save", style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold))
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(
                    "Cancel",
                    style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold),
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        },
        shape = RoundedCornerShape(24.dp),
        containerColor = MaterialTheme.colorScheme.surface,
        tonalElevation = 8.dp
    )

    if (showSideRaffleInfo)
        InfoAlertDialog(
            title = "Ticket Type",
            message = """
            • Main Event — The primary Bash or Event ticket that grants access to the event.
            
            • Side Raffle — Additional raffles usually held during the event for smaller prizes or giveaways. Selecting this type will automatically create a side raffle linked to this ticket in your 'Side Raffles' list.
            
            • 50/50 — A raffle where the total pot is split evenly — 50% goes to the winner and 50% to the organization. Selecting this type will automatically create a side raffle linked to this ticket in your 'Side Raffles' list and let's us keep track of the jackpot amount to share with your event information.
        """.trimIndent(),
            onDismiss = { showSideRaffleInfo = false }
        )

    if (showSerialNumberInfo)
        InfoAlertDialog(
            title = "Serial Number",
            message = "If you're using pre-numbered physical games like pull tabs or tip jars, enter the serial number printed on the game set here. This helps track which specific game this ticket belongs to.",
            onDismiss = { showSerialNumberInfo = false }
        )

    if (showShippingInfo)
        InfoAlertDialog(
            title = "Shipping Fee",
            message = "This optional flat-rate fee covers postage for mailed physical tickets. Applied once per purchase (not per ticket). Set to 0 if not used.",
            onDismiss = { showShippingInfo = false },
            confirmText = "Understood"
        )

    if (showAgeInfo)
        InfoAlertDialog(
            title = "Age Restriction",
            message = "Specify the minimum age required to purchase this ticket. Enter 0 for no restrictions. For prizes with additional age limits (alcohol, firearms, etc.), note them clearly in the ticket and/or event description.",
            onDismiss = { showAgeInfo = false }
        )
}